// ================================================================
// vm-button-next
//
// Fully encapsulated button styles following the Vumedi Moderat
// design system. All visual variants are driven by host attributes
// (:host([attribute])) — no CSS class maps needed.
//
// Color architecture: primitive → semantic → component
//   $primitiveColors.brand.600  →  $colors.background.bg-brand-solid  →  get-color(background, bg-brand-solid)
// ================================================================

@use "Styles/mixins" as mixins;

// -- Host --------------------------------------------------------

:host {
    display: inline-flex;
    vertical-align: middle;
}

:host([hidden]) {
    display: none !important;
}

:host([full-width]) {
    display: flex;
    width: 100%;
}

// -- Interactive element base ------------------------------------

button,
a {
    // Reset
    appearance: none;
    background: none;
    border: 1px solid transparent;
    margin: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    text-decoration: none;
    cursor: pointer;

    // Layout
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    overflow: hidden;

    // Shape
    border-radius: 9999px;

    // Typography
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0;

    // Motion
    transition:
        background-color 0.15s ease,
        color 0.15s ease,
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:focus {
        outline: none;
    }
}

// -- Content layout ----------------------------------------------

.content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.label {
    padding: 0 2px;
}

// -- Spinner -----------------------------------------------------

.spinner {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: vm-btn-spin 0.75s linear infinite;
}

@keyframes vm-btn-spin {
    to {
        transform: rotate(360deg);
    }
}

// -- Slotted icon sizing -----------------------------------------

::slotted([slot="icon-leading"]),
::slotted([slot="icon-trailing"]) {
    display: flex;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
}

// ================================================================
// Sizes
// ================================================================

:host([size="sm"]) {
    button,
    a {
        height: 36px;
        padding: 8px 12px;
    }
}

:host([size="md"]) {
    button,
    a {
        height: 40px;
        padding: 8px 12px;
    }
}

:host([size="lg"]) {
    button,
    a {
        height: 44px;
        padding: 8px 12px;
    }
}

:host([size="xl"]) {
    button,
    a {
        height: 48px;
        padding: 8px 12px;
    }
}

// ================================================================
// Icon-only modifier
// ================================================================

:host([icon-only]) {
    .label {
        display: none;
    }

    button,
    a {
        padding: 0;
    }
}

:host([icon-only][size="sm"]) {
    button,
    a {
        width: 36px;
    }
}
:host([icon-only][size="md"]) {
    button,
    a {
        width: 40px;
    }
}
:host([icon-only][size="lg"]) {
    button,
    a {
        width: 44px;
    }
}
:host([icon-only][size="xl"]) {
    button,
    a {
        width: 48px;
    }
}

// ================================================================
// Hierarchy: Primary
// ================================================================

:host([hierarchy="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid);
        color: mixins.get-color(text, text-white);
    }
}

:host([hierarchy="primary"]:hover:not([disabled]):not([loading])),
:host([hierarchy="primary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(background, bg-brand-subtle);
    }
}

// ================================================================
// Hierarchy: Secondary
// ================================================================

:host([hierarchy="secondary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle);
        color: mixins.get-color(text, text-brand-primary);
    }
}

:host([hierarchy="secondary"]:hover:not([disabled]):not([loading])),
:host([hierarchy="secondary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle-hover);
    }
}

// ================================================================
// Hierarchy: Tertiary
// ================================================================

:host([hierarchy="tertiary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(text, text-secondary);
        border-color: mixins.get-color(border, border-primary);
    }
}

:host([hierarchy="tertiary"]:hover:not([disabled]):not([loading])),
:host([hierarchy="tertiary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-primary-hover);
        color: mixins.get-color(text, text-secondary-hover);
    }
}

// ================================================================
// Hierarchy: Quaternary (ghost)
// ================================================================

:host([hierarchy="quaternary"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-tertiary);
    }
}

:host([hierarchy="quaternary"]:hover:not([disabled]):not([loading])),
:host([hierarchy="quaternary"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-primary-hover);
        color: mixins.get-color(text, text-tertiary-hover);
    }
}

// ================================================================
// Hierarchy: Link color
// ================================================================

:host([hierarchy="link-color"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-brand-secondary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-brand-primary);
    }
}

:host([hierarchy="link-color"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-brand-secondary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-brand-primary-hover);
    }
}

// ================================================================
// Hierarchy: Link gray
// ================================================================

:host([hierarchy="link-gray"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-tertiary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-quaternary);
    }
}

:host([hierarchy="link-gray"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-tertiary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-quaternary-hover);
    }
}

// ================================================================
// Link variant size overrides
//
// Link hierarchies use the text line-height as their height
// instead of the padded button heights.
// ================================================================

:host([hierarchy="link-color"][size="sm"]),
:host([hierarchy="link-gray"][size="sm"]),
:host([hierarchy="link-error"][size="sm"]),
:host([hierarchy="link-color"][size="md"]),
:host([hierarchy="link-gray"][size="md"]),
:host([hierarchy="link-error"][size="md"]) {
    button,
    a {
        height: 20px;
    }
}

:host([hierarchy="link-color"][size="lg"]),
:host([hierarchy="link-gray"][size="lg"]),
:host([hierarchy="link-error"][size="lg"]),
:host([hierarchy="link-color"][size="xl"]),
:host([hierarchy="link-gray"][size="xl"]),
:host([hierarchy="link-error"][size="xl"]) {
    button,
    a {
        height: 24px;
    }
}

// ================================================================
// Error hierarchies
//
// Destructive variants that recolor a base shape with the error palette:
//   primary-error    → solid red   (Figma "Primary destructive")
//   tertiary-error   → red outline  (Figma "Secondary destructive")
//   quaternary-error → red ghost    (Figma "Tertiary destructive")
// Structural styles (sizing, border, radius) come from the size/base rules
// above — they aren't hierarchy-keyed — so these rules only set colors.
// Disabled stays the standard gray (the disabled rules below come later in
// source order and win), so it isn't repeated here.
// ================================================================

// -- Primary error (solid red) --
:host([hierarchy="primary-error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid);
        color: mixins.get-color(text, text-white);
    }
}

:host([hierarchy="primary-error"]:hover:not([disabled]):not([loading])),
:host([hierarchy="primary-error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(background, bg-error-primary-hover);
    }
}

// -- Tertiary error (red outline) --
:host([hierarchy="tertiary-error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(text, text-error-primary);
        border-color: mixins.get-color(border, border-error-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([hierarchy="tertiary-error"]:hover:not([disabled]):not([loading])),
:host([hierarchy="tertiary-error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-primary);
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// -- Quaternary error (red ghost) --
:host([hierarchy="quaternary-error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-error-primary);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([hierarchy="quaternary-error"]:hover:not([disabled]):not([loading])),
:host([hierarchy="quaternary-error"]:active:not([disabled]):not([loading])) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-primary);
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// -- Link error (red link) -- mirrors the link-color layout (transparent bg,
// no padding, radius 0); only the palette differs. Size + focus-ring radius
// overrides are shared with the other link hierarchies further down.
:host([hierarchy="link-error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(text, text-error-primary);
        border-radius: 0;
        padding: 0;
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-secondary);
    }
}

:host([hierarchy="link-error"]:hover:not([disabled]):not([loading])) {
    button,
    a {
        color: mixins.get-color(text, text-error-primary-hover);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-error-primary);
    }
}

// Loading reuses the hover background for Primary error (mirrors the brand
// Primary loading treatment).
:host([loading][hierarchy="primary-error"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-error-solid-hover);
        color: mixins.get-color(text, text-white);
    }
}

// ================================================================
// Disabled state
// ================================================================

:host([disabled]) {
    button {
        cursor: not-allowed;
    }

    a {
        cursor: not-allowed;
        pointer-events: none;
    }
}

:host([disabled][hierarchy="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-disabled);
        color: mixins.get-color(foreground, fg-disabled);
        border-color: mixins.get-color(border, border-disabled-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

:host([disabled][hierarchy="secondary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-subtle);
        color: mixins.get-color(foreground, fg-disabled);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled);
    }
}

:host([disabled][hierarchy="tertiary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-white);
        color: mixins.get-color(foreground, fg-disabled);
        border-color: mixins.get-color(border, border-disabled-subtle);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

:host([disabled][hierarchy="quaternary"]),
:host([disabled][hierarchy="link-color"]),
:host([disabled][hierarchy="link-gray"]),
:host([disabled][hierarchy="link-error"]) {
    button,
    a {
        background-color: transparent;
        color: mixins.get-color(foreground, fg-disabled);
    }

    ::slotted([slot="icon-leading"]),
    ::slotted([slot="icon-trailing"]) {
        color: mixins.get-color(foreground, fg-disabled-subtle);
    }
}

// ================================================================
// Loading state
// ================================================================

:host([loading]) {
    button,
    a {
        pointer-events: none;
        cursor: wait;
    }

    .content {
        visibility: hidden;
    }
}

// Loading uses the hover background for Primary
:host([loading][hierarchy="primary"]) {
    button,
    a {
        background-color: mixins.get-color(background, bg-brand-solid-hover);
        color: mixins.get-color(text, text-white);
    }
}

// ================================================================
// Focus ring
//
// Double box-shadow: inner white ring + outer brand ring.
// box-shadow is NOT clipped by overflow:hidden on the element.
// ================================================================

button:focus-visible,
a:focus-visible {
    box-shadow:
        0 0 0 2px mixins.get-color(background, bg-white),
        0 0 0 4px mixins.get-color(foreground, fg-brand-primary);
}

// Link variants use a smaller border-radius for the focus ring
:host([hierarchy="link-color"]),
:host([hierarchy="link-gray"]),
:host([hierarchy="link-error"]) {
    button:focus-visible,
    a:focus-visible {
        border-radius: 4px;
    }
}

// Error hierarchies use the error focus ring (outer ring = error color).
:host([hierarchy="primary-error"]),
:host([hierarchy="tertiary-error"]),
:host([hierarchy="quaternary-error"]),
:host([hierarchy="link-error"]) {
    button:focus-visible,
    a:focus-visible {
        box-shadow:
            0 0 0 2px mixins.get-color(background, bg-white),
            0 0 0 4px mixins.get-color(foreground, fg-error-secondary);
    }
}
